import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { Button, Checkbox, Input, InputNumber, Modal, Select, Spin, Typography } from "antd";
import { useStoreFileMutation } from "api/File/hooks.ts";
import queryClient from "api/queryClient.ts";
import { useRequestTenantDeletionMutation, useUpdateTenantMutation } from "api/Tenant/hooks.ts";
import {
    isWorkspaceCurrencyReconverting,
    useGetSettingsQuery,
    useGetWorkspaceCurrencyQuery,
    useUpdateSettingMutation,
    useUpdateWorkspaceCurrencyMutation,
} from "api/TenantSlug/Setting/hooks";
import { AxiosError } from "axios";
import { Collapseable } from "components/UI/Collapseable/Collapseable";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AffiliateBillingSelectSearchable } from "components/UI/Selects/AffiliateBillingSelectSearchable/AffiliateBillingSelectSearchable.tsx";
import { CountrySelectSearchable } from "components/UI/Selects/CountrySelectSearchable/CountrySelectSearchable.tsx";
import { CurrencySelectSearchable } from "components/UI/Selects/CurrencySelectSearchable/CurrencySelectSearchable.tsx";
import { TenantCategorySelectSearchable } from "components/UI/Selects/TenantCategorySelectSearchable/TenantCategorySelectSearchable.tsx";
import { FAVICON_ACCEPTED_TYPES, UploadFormItem } from "components/UI/UploadFormItem/UploadFormItem.tsx";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { DATE_FORMAT } from "localConstants";
import { FC, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum, TenantSettingAffiliateBilling } from "types/enums.ts";
import { ApiErrorResponse, FormId } from "types/models";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

import { schema, TenantSettingsFormData } from "./formSchema";
import { buildSettingsPayload, getSettingsDefaultValues } from "./utils";

const { Title } = Typography;

interface TenantSettingsFormProps {
    tenant: App.Data.TenantData;
}

const domainOptions = [{ value: "portal.leadgenia.com", label: "portal.leadgenia.com" }];
const selectApprovalOptions = [
    { value: "manual_approval", label: "Manual approval" },
    { value: "automatic_approval", label: "Automatic approval" },
];
const templateOptions = [{ value: "lending", label: "Lending" }];

interface TenantSettingsFormContentProps {
    onLogoSelected: (file: File | null) => void;
    onFaviconSelected: (file: File | null) => void;
    currencyDisabled: boolean;
    currencyHelpText?: string;
}

export const TenantSettingsFormContent: FC<TenantSettingsFormContentProps> = ({
    onLogoSelected,
    onFaviconSelected,
    currencyDisabled,
    currencyHelpText,
}) => {
    const { t } = usePaldockTranslation("forms.tenant-settings");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, fieldNames } = useGeneralForm<TenantSettingsFormData>();
    const webhookCustomParameters = useWatch({ control, name: "webhook_custom_parameters" });
    const showAffiliateProgram = useWatch({ control, name: "show_affiliate_program" });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left section - Customization */}
            <div className="bg-white rounded-lg p-4">
                <Title level={3} className="!mb-5">
                    {t("sections.customization.title")}
                </Title>
                <FormCol>
                    <FormGrid cols={3}>
                        <FormItem
                            control={control}
                            name={fieldNames.title.name}
                            label={tFields("title.label")}
                            required
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.slug_name.name}
                            label={t("fields.slug_name.label")}
                            required
                        >
                            <Input />
                        </FormItem>
                        <FormItem control={control} name={fieldNames.domain.name} label={t("fields.domain.label")}>
                            <Select options={domainOptions} disabled />
                        </FormItem>
                    </FormGrid>
                    <FormGrid cols={3}>
                        <UploadFormItem
                            control={control}
                            label={t("fields.logo.label")}
                            name="logo"
                            onFileSelected={onLogoSelected}
                        />
                        <UploadFormItem
                            control={control}
                            label={t("fields.favicon.label")}
                            name="favicon"
                            acceptedTypes={FAVICON_ACCEPTED_TYPES}
                            minDimensions={{ width: 50, height: 50 }}
                            onFileSelected={onFaviconSelected}
                        />
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem control={control} name={fieldNames.template.name} label={t("fields.template.label")}>
                            <Select options={templateOptions} disabled />
                        </FormItem>
                    </FormGrid>
                </FormCol>
            </div>

            {/* Right section - Default settings */}
            <div className="bg-white rounded-lg p-4">
                <Title level={3} className="!mb-5">
                    {t("sections.default_settings.title")}
                </Title>
                <FormCol>
                    <FormGrid cols={3}>
                        <FormItem
                            control={control}
                            name={fieldNames.country_id?.name}
                            label={t("fields.default_country.label")}
                            required
                        >
                            <CountrySelectSearchable />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.currency_id?.name}
                            label={tFields("currency.label")}
                            required
                            extra={currencyHelpText}
                        >
                            <CurrencySelectSearchable disabled={currencyDisabled} />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.minimum_payment.name}
                            label={t("fields.minimum_payment.label")}
                        >
                            <InputNumber disabled className="w-full" addonAfter="Kč" />
                        </FormItem>
                    </FormGrid>
                    <FormGrid cols={3}>
                        <FormItem
                            control={control}
                            name={fieldNames.affiliate_billing.name}
                            label={t("fields.affiliate_billing.label")}
                        >
                            <AffiliateBillingSelectSearchable />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.new_partner_approval.name}
                            label={t("fields.new_partner_approval.label")}
                        >
                            <Select options={selectApprovalOptions} disabled />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={fieldNames.new_advertiser_approval.name}
                            label={t("fields.new_advertiser_approval.label")}
                        >
                            <Select options={selectApprovalOptions} disabled />
                        </FormItem>
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem
                            control={control}
                            name={fieldNames.show_affiliate_program.name}
                            valuePropName="checked"
                        >
                            <Checkbox>{t("fields.show_affiliate_program.label")}</Checkbox>
                        </FormItem>
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem
                            control={control}
                            name={fieldNames.affiliate_categories.name}
                            label={t("fields.affiliate_categories.label")}
                            required={showAffiliateProgram}
                        >
                            <TenantCategorySelectSearchable mode="multiple" />
                        </FormItem>
                        {/* <FormItem control={control} name={fieldNames.language_id?.name} label={t("fields.language_id.label")} required>
                        <LanguageSelectSearchable disabled />
                    </FormItem> */}
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem
                            control={control}
                            name={fieldNames.webhook_custom_parameters.name}
                            valuePropName="checked"
                        >
                            <Checkbox>{t("fields.webhook_custom_parameters.label")}</Checkbox>
                        </FormItem>
                    </FormGrid>
                    <Collapseable
                        key={String(webhookCustomParameters)}
                        title={t("sections.webhook_mapping.title")}
                        defaultActiveKey={webhookCustomParameters ? ["1"] : []}
                        wrapperClassName="!p-0"
                        className="!p-0 [&_.ant-collapse-header]:!p-0 [&_.ant-collapse-header]:!pb-4"
                    >
                        <FormGrid cols={3}>
                            <FormItem
                                control={control}
                                name={fieldNames.webhook_eid.name}
                                label={t("fields.webhook_eid.label")}
                                tooltip={t("fields.webhook_mapping_tooltip")}
                            >
                                <Input placeholder={t("fields.webhook_mapping_placeholder")} />
                            </FormItem>
                            <FormItem
                                control={control}
                                name={fieldNames.webhook_advertiser_id.name}
                                label={t("fields.webhook_advertiser_id.label")}
                                tooltip={t("fields.webhook_mapping_tooltip")}
                            >
                                <Input placeholder={t("fields.webhook_mapping_placeholder")} />
                            </FormItem>
                            <FormItem
                                control={control}
                                name={fieldNames.webhook_advertiser_custom_id.name}
                                label={t("fields.webhook_advertiser_custom_id.label")}
                                tooltip={t("fields.webhook_mapping_tooltip")}
                            >
                                <Input placeholder={t("fields.webhook_mapping_placeholder")} />
                            </FormItem>
                        </FormGrid>
                    </Collapseable>
                </FormCol>
            </div>
        </div>
    );
};

interface TenantSettingsFormInnerProps {
    onLogoSelected: (file: File | null) => void;
    onFaviconSelected: (file: File | null) => void;
    currencyDisabled: boolean;
    currencyHelpText?: string;
}

const TenantSettingsFormInner: FC<TenantSettingsFormInnerProps> = ({
    onLogoSelected,
    onFaviconSelected,
    currencyDisabled,
    currencyHelpText,
}) => {
    return (
        <TenantSettingsFormContent
            onLogoSelected={onLogoSelected}
            onFaviconSelected={onFaviconSelected}
            currencyDisabled={currencyDisabled}
            currencyHelpText={currencyHelpText}
        />
    );
};

export const TenantSettingsForm: FC<TenantSettingsFormProps> = ({ tenant }) => {
    const { t } = usePaldockTranslation("forms.tenant-settings");
    const { t: tC } = usePaldockTranslation("common");
    const userUtils = useUser();
    const permissionUtils = usePermission();
    const canUpdateCurrency = permissionUtils.hasPermission(PermissionEnum.WorkspaceCurrencyUpdate);
    const navigate = useNavigate();
    const [slugChanged, setSlugChanged] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteSlugInput, setDeleteSlugInput] = useState("");
    const formId = FormId.TenantSettingsForm;

    const pendingLogoFile = useRef<File | null>(null);
    const logoCleared = useRef(false);
    const pendingFaviconFile = useRef<File | null>(null);
    const faviconCleared = useRef(false);

    const requestDeletionMutation = useRequestTenantDeletionMutation();
    const storeFileMutation = useStoreFileMutation();

    const getSettingsQuery = useGetSettingsQuery();
    const workspaceCurrencyQuery = useGetWorkspaceCurrencyQuery();

    const updateSettingMutation = useUpdateSettingMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getSettings"] });
        },
    });

    const updateWorkspaceCurrencyMutation = useUpdateWorkspaceCurrencyMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWorkspaceCurrency"] });
        },
    });

    const updateTenantMutation = useUpdateTenantMutation({
        onSuccess: async (response) => {
            queryClient.invalidateQueries({ queryKey: ["getTenant", tenant.id] });
            queryClient.invalidateQueries({ queryKey: ["getTenants"] });
            queryClient.invalidateQueries({ queryKey: ["me"] });
            queryClient.invalidateQueries({ queryKey: ["getUser", userUtils.getAuthenticatedUser()?.id] });
            queryClient.invalidateQueries({ queryKey: ["users"] });

            toast.success(t("messages.update_success"));
            await userUtils.fetchUser();
            if (slugChanged) {
                // Update tenant state with new slug before navigation
                userUtils.setTenant(response.tenant);
                await navigate({
                    to: "/$tenantSlug/setting/general",
                    params: {
                        tenantSlug: response.tenant.slug_name,
                    },
                    replace: true,
                });
            }
        },
    });

    const defaultValues = useMemo<TenantSettingsFormData>(() => {
        const settingsDefaults = getSettingsDefaultValues(getSettingsQuery.data?.settings || []);
        return {
            title: tenant.title,
            slug_name: tenant.slug_name,
            domain: "",
            template: templateOptions[0].value,
            minimum_payment: 500,
            new_partner_approval: "manual_approval",
            new_advertiser_approval: "manual_approval",
            tags: ["Super", "Private"],
            test: "",
            terms_and_conditions: "",
            affiliate_billing: settingsDefaults.affiliate_billing ?? TenantSettingAffiliateBilling.UninvoicedBalance,
            affiliate_categories: tenant.catalogCategories?.map((c) => c.category) ?? [],
            show_affiliate_program: tenant.show_in_public_catalog ?? false,
            logo: settingsDefaults.logo,
            favicon: settingsDefaults.favicon,
            country_id: settingsDefaults.country_id ?? 1,
            webhook_custom_parameters: settingsDefaults.webhook_custom_parameters ?? false,
            webhook_eid: settingsDefaults.webhook_eid ?? "",
            webhook_advertiser_id: settingsDefaults.webhook_advertiser_id ?? "",
            webhook_advertiser_custom_id: settingsDefaults.webhook_advertiser_custom_id ?? "",
            // No fallback — isLoading below keeps the form unrendered until workspaceCurrencyQuery
            // resolves, so the server value is always the one the user sees.
            currency_id: workspaceCurrencyQuery.data?.currency_id as number,
            // Temporarily hardcoded values
            language_id: 39, // English
        };
    }, [tenant, getSettingsQuery.data, workspaceCurrencyQuery.data]);

    const defaultValuesKey = JSON.stringify([
        tenant.id,
        tenant.slug_name,
        getSettingsQuery.data?.settings,
        workspaceCurrencyQuery.data?.currency_id,
    ]);

    const availableAt = workspaceCurrencyQuery.data?.available_at;
    const currencyReconverting = isWorkspaceCurrencyReconverting(workspaceCurrencyQuery.data);
    const currencyCooldownActive = !!availableAt && new Date(availableAt) > new Date();
    // !canUpdateCurrency also gates the mutateAsync call further down — a user who can view
    // this page (SettingsUpdate) but lacks WorkspaceCurrencyUpdate must never have the unrelated
    // "save settings" click quietly resubmit currency and blow up with an unrelated 403.
    const currencyDisabled = !canUpdateCurrency || currencyReconverting || currencyCooldownActive;
    const currencyHelpText = !canUpdateCurrency
        ? undefined
        : currencyReconverting
          ? t("fields.currency.reconverting_help")
          : currencyCooldownActive && availableAt
            ? t("fields.currency.cooldown_help", { date: dayjs(availableAt).format(DATE_FORMAT) })
            : undefined;

    const uploadPendingFile = async (file: File, type: string): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - formData as StoreFileRequest
        const res = await storeFileMutation.mutateAsync(formData);
        return res.path;
    };

    const onSubmit = async (data: TenantSettingsFormData) => {
        const currencyChanging = !currencyDisabled && data.currency_id !== workspaceCurrencyQuery.data?.currency_id;

        if (currencyChanging) {
            const confirmed = await new Promise<boolean>((resolve) => {
                Modal.confirm({
                    title: t("messages.currency_change_confirm_title"),
                    content: t("messages.currency_change_confirm_description", {
                        days: workspaceCurrencyQuery.data?.cooldown_days ?? 180,
                    }),
                    okText: tC("save"),
                    cancelText: tC("cancel"),
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });

            if (!confirmed) {
                return;
            }
        }

        if (data.slug_name !== tenant.slug_name) {
            setSlugChanged(true);
        }

        try {
            if (pendingLogoFile.current) {
                data.logo = await uploadPendingFile(pendingLogoFile.current, "tenant_logo");
                pendingLogoFile.current = null;
                logoCleared.current = false;
            } else if (logoCleared.current) {
                data.logo = null;
                logoCleared.current = false;
            } else {
                data.logo = undefined;
            }

            if (pendingFaviconFile.current) {
                data.favicon = await uploadPendingFile(pendingFaviconFile.current, "tenant_favicon");
                pendingFaviconFile.current = null;
                faviconCleared.current = false;
            } else if (faviconCleared.current) {
                data.favicon = null;
                faviconCleared.current = false;
            } else {
                data.favicon = undefined;
            }

            const settingsPayload = buildSettingsPayload(data, getSettingsQuery.data?.settings || []);

            await updateSettingMutation.mutateAsync({ settings: settingsPayload });
            await updateTenantMutation.mutateAsync({
                tenantId: tenant.id,
                updateTenantRequest: {
                    title: data.title,
                    active: tenant.active,
                    slug_name: data.slug_name,
                    show_in_public_catalog: data.show_affiliate_program,
                    catalog_categories: data.affiliate_categories,
                },
            });
        } catch (e) {
            toast.error(t("messages.update_error"));
            return;
        }

        // Separate endpoint on purpose (cooldown + background report reconversion) — kept out of
        // the try/catch above so a currency-specific failure (cooldown/in-progress) doesn't read
        // as a generic "settings update failed" and doesn't block the rest of the form save. Only
        // called when the value is actually changing — the BE would no-op an unchanged resubmit
        // anyway, but there's no reason to make the request at all when nothing changed.
        if (currencyChanging) {
            try {
                await updateWorkspaceCurrencyMutation.mutateAsync({ currency_id: data.currency_id });
            } catch (e) {
                toast.error(
                    extractValidationErrorMessages(e as AxiosError<ApiErrorResponse>)[0] ?? t("messages.update_error")
                );
            }
        }
    };

    const handleDeleteWorkspace = async () => {
        try {
            await requestDeletionMutation.mutateAsync({
                tenantId: tenant.id,
                request: {
                    return_url: `${import.meta.env.VITE_APP_URL}/${tenant.slug_name}/confirm-deletion`,
                },
            });
            setDeleteModalOpen(false);
            setDeleteSlugInput("");
            toast.success(t("actions.delete_success"));
        } catch {
            // Modal intentionally stays open so the user can retry without re-entering the slug
            toast.error(t("actions.delete_error"));
        }
    };

    const handleCloseDeleteModal = () => {
        if (requestDeletionMutation.isPending) {
            return;
        }
        setDeleteModalOpen(false);
        setDeleteSlugInput("");
    };

    const isLoading =
        !getSettingsQuery.data ||
        getSettingsQuery.isLoading ||
        getSettingsQuery.isFetching ||
        !workspaceCurrencyQuery.data ||
        workspaceCurrencyQuery.isLoading ||
        updateSettingMutation.isPending ||
        updateTenantMutation.isPending ||
        updateWorkspaceCurrencyMutation.isPending;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <>
            <GeneralFormProvider<TenantSettingsFormData>
                key={defaultValuesKey}
                id={formId}
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmit}
            >
                <TenantSettingsFormInner
                    onLogoSelected={(f) => {
                        pendingLogoFile.current = f;
                        logoCleared.current = f === null;
                    }}
                    onFaviconSelected={(f) => {
                        pendingFaviconFile.current = f;
                        faviconCleared.current = f === null;
                    }}
                    currencyDisabled={currencyDisabled}
                    currencyHelpText={currencyHelpText}
                />
            </GeneralFormProvider>

            {/* Danger Zone */}
            <div className="mt-6 rounded-lg border-l-4 border-lightRed bg-lightestRed p-4">
                <div className="mb-3 flex items-center gap-2">
                    <ExclamationCircleOutlined className="text-lightRed" />
                    <Typography.Title level={5} className="!mb-0 !text-red">
                        {t("sections.danger_zone.title")}
                    </Typography.Title>
                </div>
                <div className="flex items-start justify-between gap-4">
                    <Typography.Text className="text-gray-600">
                        {t("sections.danger_zone.delete_description")}
                    </Typography.Text>
                    <Button
                        danger
                        onClick={() => setDeleteModalOpen(true)}
                        loading={requestDeletionMutation.isPending}
                        className="shrink-0"
                    >
                        {t("actions.delete_workspace")}
                    </Button>
                </div>
            </div>

            {/* Delete confirmation modal */}
            <Modal
                destroyOnClose
                closable={!requestDeletionMutation.isPending}
                maskClosable={!requestDeletionMutation.isPending}
                keyboard={!requestDeletionMutation.isPending}
                title={
                    <span className="flex items-center gap-2 text-red">
                        <ExclamationCircleOutlined />
                        {t("sections.danger_zone.delete_title")}
                    </span>
                }
                open={deleteModalOpen}
                onCancel={handleCloseDeleteModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseDeleteModal} disabled={requestDeletionMutation.isPending}>
                        {tC("cancel")}
                    </Button>,
                    <Button
                        key="delete"
                        danger
                        type="primary"
                        disabled={deleteSlugInput !== tenant.slug_name}
                        loading={requestDeletionMutation.isPending}
                        onClick={handleDeleteWorkspace}
                    >
                        {tC("delete")}
                    </Button>,
                ]}
            >
                <Typography.Title level={4} className="!mb-3">
                    {tenant.title}
                </Typography.Title>
                <p>{t("actions.delete_confirm_message")}</p>
                <div className="mt-4">
                    <Typography.Text strong>{tC("workspace_identifier")}</Typography.Text>
                    <div className="mt-1">
                        <Typography.Text copyable code>
                            {tenant.slug_name}
                        </Typography.Text>
                    </div>
                </div>
                <div className="mt-3">
                    <Typography.Text strong>{tC("paste_to_confirm")}</Typography.Text>
                    <Input
                        className="mt-2"
                        placeholder={tenant.slug_name}
                        value={deleteSlugInput}
                        onChange={(e) => setDeleteSlugInput(e.target.value)}
                    />
                </div>
            </Modal>
        </>
    );
};
